import React, { useCallback, useEffect, useState } from "react";
import { HeaderImage, SearchBar, SpinLoad, LoginComponent } from "@bindeleddet/core/components";
import { Case } from "@bindeleddet/core/types";
import CaseBankImage from "assets/images/headerImage/CaseBankImage.png";
import axios from "axios";
import { isCompanyUser, useLoginContext } from "@bindeleddet/core/contexts/login";
import { Col, Row, Empty, List, Typography, Result, Spin } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { getFilteredCases, getCasebankStatusMessage } from "./helpers";
import SingleCaseBox from "./SingelCaseBox/SingleCaseBox";
import CasebankFilter from "./CasebankFilter/CasebankFilter";

const { Title } = Typography;

const Casebank = () => {
  const [cases, setCases] = useState<Case[]>();
  const [searchTerm, setSearchTerm] = useState("");
  const [sectorsChecked, setSectorChecked] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const { currentUser, isLoadingCurrentUser } = useLoginContext();

  const toggleCheckedSectors = useCallback(
    (id: number) =>
      setSectorChecked(previousState => {
        if (previousState.includes(id)) return previousState.filter(x => x !== id);
        return [...previousState, id];
      }),
    []
  );

  const handleSearch = (newSearchTerm: React.SetStateAction<string>) => {
    setSearchTerm(newSearchTerm);
  };

  useEffect(() => {
    if (!currentUser) return;
    axios
      .get<Case[]>("/website-contents/cases/")
      .then(response => {
        setCases(response.data);
        setLoading(false);
      })
      .then(() => setLoading(false));
  }, [currentUser]);

  const filteredCases =
    cases &&
    getFilteredCases(cases, searchTerm, sectorsChecked).map(singleCase => {
      return (
        <Col key={singleCase.id} style={{ marginBottom: "1em" }}>
          <SingleCaseBox
            id={singleCase.id}
            title={singleCase.title}
            description={singleCase.description}
            file={singleCase.file}
            companyId={singleCase.company}
            image={singleCase.image}
          />
        </Col>
      );
    });

  if (!currentUser || isCompanyUser(currentUser) || isLoadingCurrentUser)
    return (
      <>
        <HeaderImage imageLink={CaseBankImage} alt="HeaderImage" header="Casebank" />
        {isLoadingCurrentUser ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <Title level={3} style={{ textAlign: "center", margin: "2em 16px 0 16px" }}>
              {isCompanyUser(currentUser)
                ? "Casebanken er kun tilgjengelig for studenter"
                : "Casebanken er kun tilgjengelig for innloggede studenter"}
            </Title>
            <Result
              style={{ maxWidth: "400px", margin: "0 auto" }}
              icon={<LockOutlined />}
              subTitle={
                isCompanyUser(currentUser)
                  ? "Du er logget inn som bedrift. Logg inn som student for å få tilgang til casebanken."
                  : "Logg inn som student her for å få tilgang til casebanken."
              }
              extra={!currentUser ? <LoginComponent text="Logg inn" type="primary" /> : null}
            />
          </>
        )}
      </>
    );

  return (
    <>
      <HeaderImage imageLink={CaseBankImage} alt="HeaderImage" header="Casebank" />
      <Row justify="center" style={{ paddingLeft: "5%", paddingRight: "5%", marginTop: "40px", marginBottom: "40px" }}>
        <Col xs={24} sm={24} md={19} lg={19}>
          <SearchBar value={searchTerm} handleSearch={handleSearch} placeholder="Søk..." />
        </Col>
      </Row>
      <Row style={{ paddingLeft: "5%", paddingRight: "5%" }}>
        <Col xs={24} sm={24} md={6} lg={6}>
          <CasebankFilter
            toggleCheckedSectors={toggleCheckedSectors}
            sectorsChecked={sectorsChecked}
            onFilterReset={() => {
              setSectorChecked([]);
              handleSearch("");
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={1} lg={1} style={{ minHeight: "2em" }} />
        <Col xs={24} sm={24} md={17} lg={17}>
          <List
            itemLayout="vertical"
            pagination={{
              pageSize: 20,
              responsive: true,
              defaultCurrent: 1,
              defaultPageSize: 50,
              pageSizeOptions: ["20", "50"],
              hideOnSinglePage: true,
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={cases ? getCasebankStatusMessage(cases) : "Ingen case er tilgjengelige for øyeblikket"}
                />
              ),
            }}
            dataSource={filteredCases}
            renderItem={item => item}
            loading={{ indicator: <SpinLoad />, spinning: loading }}
          />
          <br />
        </Col>
      </Row>
    </>
  );
};

export default Casebank;
